/*
 * snd-card-actions.js — SND-16780
 * -------------------------------------------------------------------------
 * Wires Soundium's OWN wishlist + compare buttons (snippets/snd-card-actions.liquid
 * on product cards, blocks/soundium-wishlist-share.liquid on the PDP) to the two
 * apps that are already live on the storefront. Both contracts were read from the
 * shipped app bundles, not guessed:
 *
 * WISHLIST — Wishlist King (Appmate) exposes a headless global `window.WishlistKing`:
 *   .addWishlistItem({ productHandle, variantId })  -> { wishlistItem: { id, ... } }
 *   .removeWishlistItem({ wishlistItemId })
 *   ._state.observeWishlist({ wishlistId: 'mine' }).subscribe(w => w.items[].product.handle)
 *   NOTE: the app's own "collection buttons" embed targets a Dawn selector
 *   (`.card .card__heading > .full-unstyled-link`) that Horizon does not have, and it
 *   never binds to `.wkh-button`. Without this file the hearts are decorative.
 *
 * COMPARE — Specification & Compare ships <sc-dynamic-compare> as an app embed on
 *   every page (localStorage key `sc:dynamic-compare-list`, max 10 products):
 *   .addProduct(handle) / .removeProduct(handle) / .containsProduct(handle) / .getCount()
 *   document events `sc:dynamic-compare:add` / `sc:dynamic-compare:remove`
 *   Adding a product reveals the app's own floating compare bar + compare dialog,
 *   so we do not need a bar of our own.
 *
 * If an app is missing, the matching buttons hide themselves (`snd-no-wishlist` /
 * `snd-no-compare` on <html>) instead of sitting there dead.
 */
(() => {
  const WISH_SEL = '.wkh-button[data-product-handle]';
  const CMP_SEL = '[data-snd-compare][data-product-handle]';
  const MAX_COMPARE = 10;

  let wishHandles = new Set();
  let wishItemIds = new Map();
  let queued = false;

  const waitFor = (get, tries = 60) =>
    new Promise((resolve) => {
      const tick = () => {
        let v = null;
        try { v = get(); } catch (e) { v = null; }
        if (v) return resolve(v);
        if (--tries <= 0) return resolve(null);
        setTimeout(tick, 250);
      };
      tick();
    });

  const label = (btn, on) => {
    const text = on ? btn.dataset.labelOn : btn.dataset.labelOff;
    if (!text) return;
    btn.setAttribute('aria-label', text);
    btn.setAttribute('title', text);
  };

  /* ---------------------------------------------------------------- wishlist */
  function syncWishlist() {
    document.querySelectorAll(WISH_SEL).forEach((btn) => {
      const on = wishHandles.has(btn.dataset.productHandle);
      btn.classList.toggle('wk-selected', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      label(btn, on);
    });
  }

  async function toggleWishlist(btn) {
    const wk = window.WishlistKing;
    if (!wk) return;
    const handle = btn.dataset.productHandle;
    if (!handle || btn.classList.contains('is-busy')) return;
    const was = wishHandles.has(handle);
    const variantId = parseInt(btn.dataset.variantId, 10);

    btn.classList.add('is-busy');
    btn.classList.toggle('wk-selected', !was); // optimistic
    btn.setAttribute('aria-pressed', was ? 'false' : 'true');
    try {
      if (was) {
        const id = wishItemIds.get(handle);
        if (id) await wk.removeWishlistItem({ wishlistItemId: id });
      } else {
        const payload = { productHandle: handle };
        if (variantId) payload.variantId = variantId;
        await wk.addWishlistItem(payload);
        btn.classList.add('snd-pop');
        setTimeout(() => btn.classList.remove('snd-pop'), 320);
      }
    } catch (error) {
      btn.classList.toggle('wk-selected', was); // roll the optimistic state back
      console.warn('[snd-card-actions] wishlist', error);
    } finally {
      btn.classList.remove('is-busy');
    }
  }

  waitFor(() => {
    const wk = window.WishlistKing;
    return wk && wk._state && typeof wk._state.observeWishlist === 'function' ? wk : null;
  }).then((wk) => {
    if (!wk) {
      document.documentElement.classList.add('snd-no-wishlist');
      return;
    }
    try {
      wk._state.observeWishlist({ wishlistId: 'mine' }).subscribe((list) => {
        wishHandles = new Set();
        wishItemIds = new Map();
        ((list && list.items) || []).forEach((item) => {
          const handle = item.product && item.product.handle;
          if (!handle) return;
          wishHandles.add(handle);
          wishItemIds.set(handle, item.id);
        });
        syncWishlist();
      });
    } catch (error) {
      document.documentElement.classList.add('snd-no-wishlist');
      console.warn('[snd-card-actions] wishlist observe', error);
    }
  });

  /* ----------------------------------------------------------------- compare */
  const compareApp = () => document.querySelector('sc-dynamic-compare');

  function syncCompare() {
    const app = compareApp();
    if (!app) return;
    let count = 0;
    try { count = app.getCount(); } catch (e) { /* app still booting */ }
    document.querySelectorAll(CMP_SEL).forEach((btn) => {
      let on = false;
      try { on = app.containsProduct(btn.dataset.productHandle); } catch (e) { /* ditto */ }
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      btn.toggleAttribute('data-compare-full', !on && count >= MAX_COMPARE);
      label(btn, on);
    });
  }

  function toggleCompare(btn) {
    const app = compareApp();
    if (!app) return;
    const handle = btn.dataset.productHandle;
    try {
      if (app.containsProduct(handle)) {
        app.removeProduct(handle);
      } else {
        if (app.getCount() >= MAX_COMPARE) {
          btn.classList.add('snd-shake');
          setTimeout(() => btn.classList.remove('snd-shake'), 420);
          return;
        }
        app.addProduct(handle);
        btn.classList.add('snd-pop');
        setTimeout(() => btn.classList.remove('snd-pop'), 320);
      }
    } catch (error) {
      console.warn('[snd-card-actions] compare', error);
    }
    syncCompare();
  }

  document.addEventListener('sc:dynamic-compare:add', syncCompare);
  document.addEventListener('sc:dynamic-compare:remove', syncCompare);
  waitFor(compareApp).then((app) => {
    if (!app) document.documentElement.classList.add('snd-no-compare');
    else syncCompare();
  });

  /* ------------------------------------------------------------------ events */
  // Capture phase: the whole card is a link, so we must win before it navigates.
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!target || !target.closest) return;
      const wish = target.closest(WISH_SEL);
      if (wish) {
        event.preventDefault();
        event.stopPropagation();
        toggleWishlist(wish);
        return;
      }
      const compare = target.closest(CMP_SEL);
      if (compare) {
        event.preventDefault();
        event.stopPropagation();
        toggleCompare(compare);
      }
    },
    true
  );

  // Cards arrive late (pagination, filters, section rendering, quick-add) — re-sync.
  const observer = new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      syncWishlist();
      syncCompare();
    });
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
